import ContactDobField from './ContactDobField'
import {
  PHONE_INPUT_MAX_LENGTH,
  sanitizePhoneInput,
} from '../../utils/phone'

const TEXT_FIELD_DEFS = {
  name:      { labelKey: 'customer.name',     fallback: 'Name' },
  phone:     { labelKey: 'customer.phone',    fallback: 'Phone', type: 'tel' },
  email:     { labelKey: 'customer.email',    fallback: 'Email', type: 'email' },
  address:   { labelKey: 'customer.address',  fallback: 'Address' },
  street:    { labelKey: 'contacts.street',   fallback: 'Street / house number' },
  ward:      { labelKey: 'contacts.ward',     fallback: 'Ward' },
  district:  { labelKey: 'contacts.district', fallback: 'District' },
  province:  { labelKey: 'contacts.province', fallback: 'Province / City' },
  latitude:  { labelKey: 'contacts.latitude',  fallback: 'Latitude' },
  longitude: { labelKey: 'contacts.longitude', fallback: 'Longitude' },
  notes:     { labelKey: 'customer.notes',    fallback: 'Notes', type: 'textarea' },
}

// Visual order preserved from the original CustomersPage edit form.
const FIELD_ORDER = ['name', 'phone', 'email', 'address', 'street', 'ward', 'district', 'province', 'latitude', 'longitude', 'gender', 'dob', 'stage', 'notes']

export default function ContactEditForm({ fields, draft, setDraft, t }) {
  const label = (key, fallback) => t?.(key, { defaultValue: fallback }) ?? fallback

  function renderTextField(key) {
    const def = TEXT_FIELD_DEFS[key]
    const type = def.type || 'text'
    return (
      <div key={key} className="flex flex-col gap-1 mb-3">
        <label className="text-xs text-gray-500">{label(def.labelKey, def.fallback)}</label>
        {type === 'textarea' ? (
          <textarea
            className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            value={draft[key] ?? ''}
            onChange={e => setDraft({ ...draft, [key]: e.target.value })}
          />
        ) : (
          <input
            type={type}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={draft[key] ?? ''}
            onChange={e => {
              const nextValue = key === 'phone' ? sanitizePhoneInput(e.target.value) : e.target.value
              setDraft({ ...draft, [key]: nextValue })
            }}
            inputMode={key === 'phone' ? 'tel' : undefined}
            maxLength={key === 'phone' ? PHONE_INPUT_MAX_LENGTH : undefined}
            required={key === 'phone' && fields.includes('stage') ? (draft.stage ?? 'customer') === 'customer' : undefined}
          />
        )}
      </div>
    )
  }

  function renderField(key) {
    if (!fields.includes(key)) return null
    if (TEXT_FIELD_DEFS[key]) return renderTextField(key)

    if (key === 'gender') {
      return (
        <div key="gender" className="flex flex-col gap-1 mb-3">
          <label className="text-xs text-gray-500">{label('contacts.gender', 'Gender')}</label>
          <select
            className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={draft.gender ?? ''}
            onChange={e => setDraft({ ...draft, gender: e.target.value })}
          >
            <option value="">{label('contacts.emptyValue', '—')}</option>
            <option value="male">{label('contacts.genderValues.male', 'Male')}</option>
            <option value="female">{label('contacts.genderValues.female', 'Female')}</option>
          </select>
        </div>
      )
    }

    if (key === 'dob') {
      return (
        <ContactDobField
          key="dob"
          label={label('contacts.dob', 'Date of birth')}
          placeholder={label('contacts.dobPlaceholder', 'DD/MM/YYYY')}
          value={draft.dob}
          onChange={next => setDraft({ ...draft, dob: next })}
        />
      )
    }

    if (key === 'stage') {
      return (
        <div key="stage" className="flex flex-col gap-1 mb-3">
          <label className="text-xs text-gray-500">{label('customer.stage', 'Stage')}</label>
          <select
            className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={draft.stage ?? 'customer'}
            onChange={e => setDraft({ ...draft, stage: e.target.value })}
          >
            <option value="follower">{label('customer.stageFollower', 'Follower')}</option>
            <option value="customer">{label('customer.stageCustomer', 'Customer')}</option>
          </select>
        </div>
      )
    }

    return null
  }

  return (
    <div className="mt-2">
      {FIELD_ORDER.map(renderField)}
    </div>
  )
}
